import React, { useEffect, useState } from 'react';
import { B } from '../constants';
import { XIcon, FacebookIcon, InstagramIcon, INSTAGRAM_GRADIENT } from './SocialIcons';
import Input from './ui/Input';
import Modal from './ui/Modal';
import { trips } from '../api';

const isMobileDevice = () => typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

const WhatsAppIcon = ({ size = 28 }) => (
  <img src="https://cdn.simpleicons.org/whatsapp/ffffff" alt="" width={size} height={size} style={{ display: 'block' }} />
);

const platformBtn = (background) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 48, height: 48, background, color: 'white',
  border: 'none', borderRadius: 12, cursor: 'pointer', padding: 0,
});

// Sharing makes the trip public (POST /trips/:id/share) and returns an
// unguessable /trip/:shareSlug link — never the numeric trip id.
export default function ShareModal({ trip, onClose, shareText }) {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [error, setError] = useState(null);
  const [instagramNote, setInstagramNote] = useState(null);

  const getShareUrl = async () => {
    if (shareUrl) return shareUrl;
    try {
      const data = await trips.share(trip.id);
      setShareUrl(data.shareUrl);
      setError(null);
      return data.shareUrl;
    } catch (err) {
      console.error('Share trip error:', err);
      setError(err.message || 'Could not create a share link');
      return null;
    }
  };

  useEffect(() => {
    getShareUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.id]);

  const text = shareText || `Check out my trip to ${trip.destination}!`;

  const handleCopy = async () => {
    const url = await getShareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Instagram has no web share URL: on mobile hand the cover photo + link to the
  // OS share sheet (where Instagram is a target); otherwise copy the link and tell the user to paste it.
  const shareToInstagram = async (url) => {
    if (isMobileDevice() && navigator.share && trip.coverImage) {
      try {
        const resp = await fetch(trip.coverImage, { mode: 'cors' });
        const blob = await resp.blob();
        const file = new File([blob], 'greatbreak-trip.jpg', { type: blob.type || 'image/jpeg' });
        if (!navigator.canShare || navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: trip.title, text: `${text} ${url}` });
          return;
        }
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('Instagram file share failed, falling back:', err);
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      setInstagramNote('Link copied — paste it into your Instagram story or bio.');
    } catch {
      setInstagramNote('Instagram has no link sharing on desktop — copy the link above and paste it into your Instagram story or bio.');
    }
  };

  const handleShare = async (platform) => {
    const url = await getShareUrl();
    if (!url) return;
    if (platform === 'x') {
      window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'noopener');
      if (onClose) onClose();
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'noopener');
      if (onClose) onClose();
    } else if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, '_blank', 'noopener');
      if (onClose) onClose();
    } else if (platform === 'instagram') {
      await shareToInstagram(url);
      if (onClose) {
        setTimeout(() => onClose(), 1500);
      }
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="📤 Share Trip" size="md">
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13, color: B.night }}>
          Public Link
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Input
            readOnly
            aria-label="Public share link"
            value={shareUrl || (error ? '' : 'Creating link…')}
            style={{ flex: 1, fontSize: 13, background: '#f9f9f9', minWidth: 0 }} />
          <button
            onClick={handleCopy}
            disabled={!shareUrl}
            style={{
              padding: '12px 20px', background: copied ? B.forest : B.sky,
              color: 'white', border: 'none', borderRadius: 10, cursor: shareUrl ? 'pointer' : 'wait',
              fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', opacity: shareUrl ? 1 : 0.7,
            }}
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        {error && (
          <div role="alert" style={{ marginTop: 8, color: '#991b1b', fontSize: 13 }}>{error}</div>
        )}
      </div>

      <div>
        <label style={{ display: 'block', marginBottom: 12, fontWeight: 600, fontSize: 13, color: B.night }}>
          Share to
        </label>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => handleShare('x')} aria-label="Share to X" title="Share to X" style={platformBtn('#000000')}>
            <XIcon size={28} color="white" />
          </button>
          <button onClick={() => handleShare('facebook')} aria-label="Share to Facebook" title="Share to Facebook" style={platformBtn('#1877F2')}>
            <FacebookIcon size={28} />
          </button>
          <button onClick={() => handleShare('whatsapp')} aria-label="Share to WhatsApp" title="Share to WhatsApp" style={platformBtn('#25D366')}>
            <WhatsAppIcon size={28} />
          </button>
          <button onClick={() => handleShare('instagram')} aria-label="Share to Instagram" title="Share to Instagram" style={platformBtn(INSTAGRAM_GRADIENT)}>
            <InstagramIcon size={28} />
          </button>
        </div>
        {instagramNote && (
          <div role="status" style={{ marginTop: 12, padding: '10px 12px', background: '#fdf2f8', color: '#9d174d', borderRadius: 10, fontSize: 13, textAlign: 'center' }}>
            {instagramNote}
          </div>
        )}
      </div>
    </Modal>
  );
}
